import React, { useCallback, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Highlighter,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Quote, Minus, Table as TableIcon, Link as LinkIcon,
  Image as ImageIcon, Code, Type, Palette, Undo, Redo,
  Smile, Video,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import type { DirectoryUserLookup, FeedUploadedMedia } from '@easyflow/shared';

interface FeedEditorProps {
  content?: string;
  contentJson?: Record<string, unknown> | null;
  onChange?: (html: string, json: Record<string, unknown>) => void;
  placeholder?: string;
  minHeight?: string;
  className?: string;
  editable?: boolean;
  onMentionUser?: (userId: string) => void;
}

interface MentionItem { id: string; label: string }
interface MentionListProps { items: MentionItem[]; command: (item: MentionItem) => void }
interface MentionListHandle { onKeyDown: (props: { event: KeyboardEvent }) => boolean }
interface SuggestionRenderProps {
  editor: import('@tiptap/react').Editor;
  range: { from: number; to: number };
  query?: string;
  items: MentionItem[];
  command: (item: MentionItem) => void;
  event?: KeyboardEvent;
  clientRect?: (() => DOMRect | null) | null;
}
interface MentionCommandArgs {
  editor: import('@tiptap/react').Editor;
  range: { from: number; to: number };
  props: MentionItem;
}

const ToolbarButton = ({ onClick, active, disabled, title, children }: {
  onClick: () => void; active?: boolean; disabled?: boolean; title: string; children: React.ReactNode;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      "h-8 w-8 p-0",
      active && "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400"
    )}
  >
    {children}
  </Button>
);

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  { label: 'Frequenti', emojis: ['😀','😂','🤣','😊','😍','🥰','😘','😎','🤩','🥳','😇','🤗','🤔','😏','😴','🤤','🥱','😬','🫡'] },
  { label: 'Gesti', emojis: ['👍','👎','👏','🙏','🤝','✌️','🤞','🤟','🤙','💪','👊','✊','🫶','❤️','🧡','💛','💚','💙','💜'] },
  { label: 'Oggetti', emojis: ['🎉','🎊','🏆','⭐','🔥','💯','✅','❌','⚡','💡','📌','📎','📝','💼','📊','📈','📅','⏰','🔔'] },
  { label: 'Natura', emojis: ['☀️','🌙','⭐','🌈','🌺','🌸','🍕','🍔','☕','🎵','🎬','🚀','✈️','🏠','💻','📱','🎯','🎪','🎨'] },
];

const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.id, label: item.label });
    }
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (props.items.length === 0) {
    return <div className="bg-white dark:bg-gray-900 border rounded-lg shadow-lg p-3 text-sm text-gray-400">Nessun utente trovato</div>;
  }

  return (
    <div ref={scrollRef} className="bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-48 overflow-auto py-1 min-w-[200px]">
      {props.items.map((item, index) => (
        <button
          key={item.id}
          onClick={() => selectItem(index)}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors",
            index === selectedIndex ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" : "hover:bg-gray-50 dark:hover:bg-gray-800"
          )}
        >
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-semibold shrink-0">
            {(item.label || '?')[0]}
          </div>
          <span className="truncate">{item.label}</span>
        </button>
      ))}
    </div>
  );
});
MentionList.displayName = 'MentionList';

function createMentionSuggestion(onMention?: (userId: string) => void) {
  return {
    items: async ({ query }: { query: string }) => {
      if (!query || query.length < 1) return [];
      try {
        const data = await apiClient.get<DirectoryUserLookup[]>(`/users/search?q=${encodeURIComponent(query)}&limit=8`);
        if (data.success && data.data) {
          return data.data.map((u) => ({
            id: u.id,
            label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id,
          }));
        }
      } catch {}
      return [];
    },
    render: () => {
      let component: ReactRenderer | null = null;
      let popup: HTMLDivElement | null = null;

      return {
        onStart: (props: SuggestionRenderProps) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });
          popup = document.createElement('div');
          popup.style.position = 'absolute';
          popup.style.zIndex = '50';
          document.body.appendChild(popup);
          popup.appendChild(component.element);
          const { view } = props.editor;
          const coords = view.coordsAtPos(props.range.from);
          popup.style.left = `${coords.left}px`;
          popup.style.top = `${coords.bottom + 4}px`;
        },
        onUpdate: (props: SuggestionRenderProps) => {
          component?.updateProps(props);
          if (popup) {
            const { view } = props.editor;
            const coords = view.coordsAtPos(props.range.from);
            popup.style.left = `${coords.left}px`;
            popup.style.top = `${coords.bottom + 4}px`;
          }
        },
        onKeyDown: (props: SuggestionRenderProps) => {
          if (props.event?.key === 'Escape') {
            popup?.remove();
            component?.destroy();
            return true;
          }
          const handle = component?.ref as MentionListHandle | null | undefined;
          return handle?.onKeyDown({ event: props.event as KeyboardEvent }) || false;
        },
        onExit: () => {
          popup?.remove();
          component?.destroy();
        },
      };
    },
    command: ({ editor, range, props }: MentionCommandArgs) => {
      editor.chain().focus().insertContentAt(range, [
        { type: 'mention', attrs: { id: props.id, label: props.label } },
        { type: 'text', text: ' ' },
      ]).run();
      if (onMention) onMention(props.id);
    },
  };
}

export default function FeedEditor({ content, contentJson, onChange, placeholder, minHeight = '150px', className, editable = true, onMentionUser }: FeedEditorProps) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: 'bg-gray-100 dark:bg-gray-800 rounded-md p-4 font-mono text-sm' } },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-600 underline cursor-pointer' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full h-auto' } }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder: placeholder || 'Scrivi qualcosa... Usa @ per menzionare qualcuno' }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded px-1 py-0.5 font-medium text-sm',
        },
        suggestion: createMentionSuggestion(onMentionUser) as unknown as Parameters<typeof Mention.configure>[0] extends { suggestion?: infer S } ? S : never,
      }),
    ],
    content: contentJson || content || '',
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML(), editor.getJSON());
      }
    },
  });

  useEffect(() => {
    if (editor && contentJson && editor.isEmpty) {
      editor.commands.setContent(contentJson);
    }
  }, [editor, contentJson]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Inserisci URL:', 'https://');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const handleImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editor || !e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await apiClient.post<FeedUploadedMedia>('/messenger/feed/posts/upload', formData);
      if (data.success && data.data?.url) {
        editor.chain().focus().setImage({ src: data.data.url }).run();
      } else {
        const url = window.prompt('Upload fallito. Inserisci URL immagine:');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }
    } catch {
      const url = window.prompt('Inserisci URL immagine:');
      if (url) editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  }, [editor]);

  const handleVideoUpload = useCallback(() => {
    videoInputRef.current?.click();
  }, []);

  const handleVideoFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editor || !e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await apiClient.post<FeedUploadedMedia>('/messenger/feed/posts/upload', formData);
      if (data.success && data.data?.url) {
        editor.chain().focus().insertContent(
          `<div class="video-embed my-3"><video controls class="rounded-lg max-w-full" src="${data.data.url}"><source src="${data.data.url}"></video></div>`
        ).run();
      } else {
        const url = window.prompt('Upload fallito. Inserisci URL video:');
        if (url) {
          editor.chain().focus().insertContent(
            `<div class="video-embed my-3"><video controls class="rounded-lg max-w-full" src="${url}"><source src="${url}"></video></div>`
          ).run();
        }
      }
    } catch {
      const url = window.prompt('Inserisci URL video:');
      if (url) {
        editor.chain().focus().insertContent(
          `<div class="video-embed my-3"><video controls class="rounded-lg max-w-full" src="${url}"><source src="${url}"></video></div>`
        ).run();
      }
    }
    e.target.value = '';
  }, [editor]);

  const addTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const insertEmoji = useCallback((emoji: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(emoji).run();
    setEmojiOpen(false);
  }, [editor]);

  if (!editor) return null;

  const COLORS = ['#000000', '#374151', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];
  const HIGHLIGHT_COLORS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#e9d5ff', '#fecaca', '#fed7aa'];

  return (
    <div className={cn("border rounded-lg overflow-hidden bg-white dark:bg-gray-900", className)}>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFileChange} />
      <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoFileChange} />

      {editable && (
        <div className="border-b bg-gray-50 dark:bg-gray-800/50 p-1 flex flex-wrap items-center gap-0.5">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annulla (Ctrl+Z)">
            <Undo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Ripeti (Ctrl+Y)">
            <Redo className="h-4 w-4" />
          </ToolbarButton>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" title="Titolo">
                <Type className="h-4 w-4 mr-1" />
                Stile
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
                Paragrafo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                <Heading1 className="h-4 w-4 mr-2" /> Titolo 1
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                <Heading2 className="h-4 w-4 mr-2" /> Titolo 2
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                <Heading3 className="h-4 w-4 mr-2" /> Titolo 3
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Grassetto (Ctrl+B)">
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Corsivo (Ctrl+I)">
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Sottolineato (Ctrl+U)">
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Barrato">
            <Strikethrough className="h-4 w-4" />
          </ToolbarButton>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Colore testo">
                <Palette className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <div className="grid grid-cols-4 gap-1 p-2">
                {COLORS.map(color => (
                  <button key={color} onClick={() => editor.chain().focus().setColor(color).run()}
                    className="w-6 h-6 rounded border" style={{ backgroundColor: color }} />
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Evidenzia">
                <Highlighter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <div className="grid grid-cols-3 gap-1 p-2">
                {HIGHLIGHT_COLORS.map(color => (
                  <button key={color} onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
                    className="w-6 h-6 rounded border" style={{ backgroundColor: color }} />
                ))}
              </div>
              <DropdownMenuItem onClick={() => editor.chain().focus().unsetHighlight().run()}>
                Rimuovi evidenziazione
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Allinea a sinistra">
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centra">
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Allinea a destra">
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Giustifica">
            <AlignJustify className="h-4 w-4" />
          </ToolbarButton>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista puntata">
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerata">
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Citazione">
            <Quote className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separatore">
            <Minus className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Blocco codice">
            <Code className="h-4 w-4" />
          </ToolbarButton>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Inserisci link">
            <LinkIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={handleImageUpload} title="Inserisci immagine">
            <ImageIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={handleVideoUpload} title="Inserisci video">
            <Video className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={addTable} title="Inserisci tabella">
            <TableIcon className="h-4 w-4" />
          </ToolbarButton>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className={cn("h-8 w-8 p-0", emojiOpen && "bg-yellow-100 text-yellow-700")} title="Emoji">
                <Smile className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start" side="bottom">
              <div className="max-h-64 overflow-auto">
                {EMOJI_CATEGORIES.map(cat => (
                  <div key={cat.label} className="p-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">{cat.label}</p>
                    <div className="grid grid-cols-10 gap-0.5">
                      {cat.emojis.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => insertEmoji(emoji)}
                          className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-lg transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <EditorContent
        editor={editor}
        className={cn(
          "prose prose-sm dark:prose-invert max-w-none p-4",
          "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[var(--editor-min-height)]",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0",
          "[&_.ProseMirror_table]:border-collapse [&_.ProseMirror_td]:border [&_.ProseMirror_th]:border",
          "[&_.ProseMirror_td]:p-2 [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-gray-100 [&_.ProseMirror_th]:dark:bg-gray-800",
          "[&_.mention]:bg-blue-100 [&_.mention]:dark:bg-blue-900/40 [&_.mention]:text-blue-700 [&_.mention]:dark:text-blue-300 [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:font-medium",
        )}
        style={{ '--editor-min-height': minHeight } as React.CSSProperties}
      />
    </div>
  );
}
